/**
 * Fase 7 — central de alertas e notificações: tipos, severidade, preferências por usuário,
 * metas por cliente e webhooks de saída do workspace.
 */
import { z } from "zod";

export const NOTIFICATION_KINDS = [
  "conta_desativada",
  "pagamento_falhou",
  "limite_proximo",
  "anuncio_reprovado",
  "gasto_acima_esperado",
  "cpa_acima_meta",
  "token_expirado",
  "falha_sincronizacao",
  "regra_executada",
  "novo_lead",
  "cobranca_falhou",
  "assinatura_cancelada",
  "teste_acabando",
  "assinatura_ativa",
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const NOTIFICATION_KIND_LABELS: Record<NotificationKind, string> = {
  conta_desativada: "Conta desativada",
  pagamento_falhou: "Pagamento falhou",
  limite_proximo: "Limite de gasto",
  anuncio_reprovado: "Anúncio reprovado",
  gasto_acima_esperado: "Gasto acima do esperado",
  cpa_acima_meta: "CPA acima da meta",
  token_expirado: "Token Meta expirado",
  falha_sincronizacao: "Falha de sincronização",
  regra_executada: "Regra executada",
  novo_lead: "Novo lead",
  cobranca_falhou: "Cobrança da assinatura falhou",
  assinatura_cancelada: "Assinatura cancelada",
  teste_acabando: "Teste grátis acabando",
  assinatura_ativa: "Assinatura ativa",
};

export const NOTIFICATION_KIND_DESCRIPTIONS: Record<NotificationKind, string> = {
  conta_desativada: "A Meta desativou uma conta de anúncio.",
  pagamento_falhou: "Uma conta está com pagamento pendente na Meta.",
  limite_proximo: "Uma conta chegou a 80% do limite de gasto (ou estourou).",
  anuncio_reprovado: "Um anúncio foi reprovado na revisão da Meta.",
  gasto_acima_esperado: "O gasto de hoje de um cliente passou do orçamento diário esperado.",
  cpa_acima_meta: "O custo por resultado/lead (ou ROAS) de um cliente saiu da meta.",
  token_expirado: "Uma conexão Meta expirou e precisa ser reconectada.",
  falha_sincronizacao: "Uma sincronização falhou depois de todas as tentativas.",
  regra_executada: "Uma regra automática executou ações (ou pediu para avisar).",
  novo_lead: "Um lead novo chegou por formulário instantâneo.",
  cobranca_falhou: "O pagamento da assinatura do IAGENIX Ads falhou ou precisa de uma ação.",
  assinatura_cancelada: "A assinatura do IAGENIX Ads foi cancelada.",
  teste_acabando: "O teste grátis do IAGENIX Ads acaba em breve.",
  assinatura_ativa: "A assinatura do IAGENIX Ads foi ativada ou trocou de plano.",
};

export const NOTIFICATION_SEVERITIES = ["info", "aviso", "erro"] as const;
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number];

/** Canal padrão de e-mail por tipo (o app está sempre ligado por padrão). */
export const NOTIFICATION_EMAIL_DEFAULTS: Record<NotificationKind, boolean> = {
  conta_desativada: true,
  pagamento_falhou: true,
  limite_proximo: true,
  anuncio_reprovado: true,
  gasto_acima_esperado: true,
  cpa_acima_meta: false,
  token_expirado: true,
  falha_sincronizacao: false,
  regra_executada: false,
  novo_lead: false,
  // Os e-mails de cobrança saem direto para o Dono/Admin (lib/billing), não pelas preferências.
  cobranca_falhou: false,
  assinatura_cancelada: false,
  teste_acabando: false,
  assinatura_ativa: false,
};

export interface NotificationDto {
  id: string;
  kind: NotificationKind;
  severity: NotificationSeverity;
  title: string;
  message: string;
  entityType: string;
  entityId: string | null;
  clientId: string | null;
  /** Dados extras (ex.: id da regra/execução, id do lead). */
  data: Record<string, unknown> | null;
  readAt: string | null;
  archivedAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface NotificationsDto {
  items: NotificationDto[];
  /** Não lidas (e não arquivadas) visíveis pelo membro, independentemente dos filtros. */
  unread: number;
  total: number;
}

export const NOTIFICATION_STATUS_FILTERS = ["nao_lidas", "lidas", "arquivadas", "todas"] as const;
export type NotificationStatusFilter = (typeof NOTIFICATION_STATUS_FILTERS)[number];

export const notificationsQuerySchema = z.object({
  kind: z.enum(NOTIFICATION_KINDS).optional(),
  severity: z.enum(NOTIFICATION_SEVERITIES).optional(),
  status: z.enum(NOTIFICATION_STATUS_FILTERS).default("todas"),
  clientId: z.uuid().optional(),
  q: z.string().trim().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type NotificationsQuery = z.infer<typeof notificationsQuerySchema>;

// ---- Preferências por usuário ---------------------------------------------------------------------

export interface NotificationChannelPrefs {
  app: boolean;
  email: boolean;
}

export interface NotificationPrefsDto {
  kinds: Record<NotificationKind, NotificationChannelPrefs>;
  /** Resumo diário por e-mail (8h de São Paulo) com o que ficou sem ler. */
  dailyDigest: boolean;
}

export function defaultNotificationPrefs(): NotificationPrefsDto {
  const kinds = {} as Record<NotificationKind, NotificationChannelPrefs>;
  for (const k of NOTIFICATION_KINDS) kinds[k] = { app: true, email: NOTIFICATION_EMAIL_DEFAULTS[k] };
  return { kinds, dailyDigest: false };
}

export const updateNotificationPrefsSchema = z.object({
  kinds: z
    .partialRecord(
      z.enum(NOTIFICATION_KINDS),
      z.object({ app: z.boolean(), email: z.boolean() }),
    )
    .optional(),
  dailyDigest: z.boolean().optional(),
});
export type UpdateNotificationPrefsInput = z.infer<typeof updateNotificationPrefsSchema>;

// ---- Metas por cliente ------------------------------------------------------------------------------

export interface ClientGoalsDto {
  clientId: string;
  /** Centavos na moeda das contas do cliente. */
  targetCpaCents: number | null;
  targetCplCents: number | null;
  targetRoas: number | null;
  expectedDailyBudgetCents: number | null;
  /** Tolerância em % antes de alertar (ex.: 20 = alerta acima de 120% da meta). */
  tolerancePct: number;
  updatedAt: string | null;
}

export const clientGoalsSchema = z.object({
  targetCpaCents: z.number().int().positive().nullable().default(null),
  targetCplCents: z.number().int().positive().nullable().default(null),
  targetRoas: z.number().positive().nullable().default(null),
  expectedDailyBudgetCents: z.number().int().positive().nullable().default(null),
  tolerancePct: z.number().int().min(0).max(500).default(20),
});
export type ClientGoalsInput = z.infer<typeof clientGoalsSchema>;

// ---- Webhooks de saída ------------------------------------------------------------------------------

/** Eventos que um webhook pode assinar: os alertas por tipo, leads e o teste. */
export const WEBHOOK_EVENTS = [...NOTIFICATION_KINDS, "lead_recebido", "teste"] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];

export const WEBHOOK_EVENT_LABELS: Record<WebhookEvent, string> = {
  ...NOTIFICATION_KIND_LABELS,
  lead_recebido: "Lead recebido (dados do lead)",
  teste: "Teste",
};

export interface WebhookDto {
  id: string;
  name: string;
  url: string;
  /** Vazio = todos os eventos. */
  events: WebhookEvent[];
  active: boolean;
  /** Só na criação (para a pessoa copiar); depois o segredo nunca volta. */
  secret?: string;
  lastDeliveryAt: string | null;
  lastDeliveryStatus: "ok" | "erro" | "pendente" | null;
  createdAt: string;
}

export interface WebhookDeliveryDto {
  id: string;
  event: WebhookEvent;
  status: "pendente" | "ok" | "erro";
  attempts: number;
  responseStatus: number | null;
  error: string | null;
  nextAttemptAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

const httpsUrl = z
  .url("Informe uma URL válida.")
  .max(2048)
  .refine((u) => u.startsWith("https://") || u.startsWith("http://localhost") || u.startsWith("http://127.0.0.1"), {
    message: "Use uma URL https:// (http só para localhost).",
  });

export const createWebhookSchema = z.object({
  name: z.string().trim().min(2, "Dê um nome ao webhook.").max(80),
  url: httpsUrl,
  /** Vazio = gerado pelo servidor. */
  secret: z.string().trim().min(16, "O segredo precisa ter pelo menos 16 caracteres.").max(200).optional(),
  events: z.array(z.enum(WEBHOOK_EVENTS)).default([]),
  active: z.boolean().default(true),
});
export type CreateWebhookInput = z.input<typeof createWebhookSchema>;

export const updateWebhookSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  url: httpsUrl.optional(),
  secret: z.string().trim().min(16).max(200).optional(),
  events: z.array(z.enum(WEBHOOK_EVENTS)).optional(),
  active: z.boolean().optional(),
});
export type UpdateWebhookInput = z.infer<typeof updateWebhookSchema>;

/** Cabeçalhos da assinatura dos webhooks de saída (HMAC-SHA256 de `${timestamp}.${corpo}`). */
export const WEBHOOK_SIGNATURE_HEADER = "X-IAGENIX-Signature";
export const WEBHOOK_TIMESTAMP_HEADER = "X-IAGENIX-Timestamp";
export const WEBHOOK_EVENT_HEADER = "X-IAGENIX-Event";
export const WEBHOOK_DELIVERY_HEADER = "X-IAGENIX-Delivery";
